// src/components/GameStatistics.tsx
// Component for handling end-of-game statistics and database storage
import React, { useState, useEffect } from "react";
import { PIECE_VALUES } from "../data/pieceValues.ts";

export interface GameStatisticsData {
  gameName: string;
  boardSize: number;
  gameResult: string;
  winningPlayerId: string;
  losingPlayerId: string;
  winningPlayerColor: string;
  losingPlayerColor: string;
  winningPlayerRanking: number;
  losingPlayerRanking: number;
  winningPlayerScore: number;
  losingPlayerScore: number;
  numberOfMoves: number;
  whitePiecesCaptured: string[];
  blackPiecesCaptured: string[];
  numWhitePiecesCaptured: number;
  numBlackPiecesCaptured: number;
  gameEndDateTime: string;
  timeLimit: number;
  roomNumber: string;
}

interface GameStatisticsProps {
  isOpen: boolean;
  statistics: GameStatisticsData | null;
  onClose: () => void;
  onSaveToDatabase?: (stats: GameStatisticsData) => Promise<void>;
}

export const GameStatistics: React.FC<GameStatisticsProps> = ({
  isOpen,
  statistics,
  onClose,
  onSaveToDatabase,
}) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);

  // Auto-save to database when statistics are provided
  useEffect(() => {
    if (isOpen && statistics && onSaveToDatabase && !saveSuccess) {
      handleSaveToDatabase();
    }
  }, [isOpen, statistics]);

  const handleSaveToDatabase = async () => {
    if (!statistics || !onSaveToDatabase) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await onSaveToDatabase(statistics);
      setSaveSuccess(true);
      console.log("✅ Game statistics saved to database successfully");
    } catch (error) {
      console.error("❌ Failed to save game statistics:", error);
      setSaveError(
        error instanceof Error ? error.message : "Failed to save statistics"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSaveSuccess(false);
    setSaveError(null);
    onClose();
  };

  if (!isOpen || !statistics) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 10000,
        overflowY: "auto",
        padding: "20px",
      }}
      onClick={handleClose}
    >
      <div
        style={{
          backgroundColor: "#f3e5f5",
          padding: "20px",
          borderRadius: "12px",
          maxWidth: "600px",
          width: "90%",
          maxHeight: "85vh",
          overflowY: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2
          style={{
            margin: "0 0 6px 0",
            color: "#6a1b9a",
            fontSize: "19px",
            textAlign: "center",
          }}
        >
          Game Statistics Summary
        </h2>
        <p
          style={{
            textAlign: "center",
            color: "#666",
            marginBottom: "12px",
            fontSize: "11px",
            lineHeight: "1.3",
          }}
        >
          End of game file and popup dialog summary that stores in backend
          database
        </p>

        {/* Save Status Indicators */}
        {isSaving && (
          <div
            style={{
              padding: "6px",
              backgroundColor: "#e3f2fd",
              borderRadius: "5px",
              marginBottom: "10px",
              textAlign: "center",
              color: "#1565c0",
              fontSize: "12px",
            }}
          >
            💾 Saving to database...
          </div>
        )}

        {saveSuccess && (
          <div
            style={{
              padding: "6px",
              backgroundColor: "#c8e6c9",
              borderRadius: "5px",
              marginBottom: "10px",
              textAlign: "center",
              color: "#2e7d32",
              fontWeight: "600",
              fontSize: "12px",
            }}
          >
            ✅ Statistics saved successfully!
          </div>
        )}

        {saveError && (
          <div
            style={{
              padding: "6px",
              backgroundColor: "#ffcdd2",
              borderRadius: "5px",
              marginBottom: "10px",
              textAlign: "center",
              color: "#c62828",
              fontSize: "12px",
            }}
          >
            ❌ Error: {saveError}
          </div>
        )}

        {/* Statistics Display */}
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <StatRow label="Game Name" value={statistics.gameName} />
          <StatRow
            label="Board Size"
            value={`${statistics.boardSize}×${statistics.boardSize}`}
          />
          <StatRow
            label="Game Result"
            value={statistics.gameResult}
            highlight
          />
          <StatRow
            label="Winning Player userId"
            value={statistics.winningPlayerId}
          />
          <StatRow
            label="Losing Player userId"
            value={statistics.losingPlayerId}
          />
          <StatRow
            label="Winning Player Color"
            value={statistics.winningPlayerColor}
          />
          <StatRow
            label="Losing Player Color"
            value={statistics.losingPlayerColor}
          />
          <StatRow
            label="Winning Player Ranking"
            value={statistics.winningPlayerRanking.toString()}
          />
          <StatRow
            label="Losing Player Ranking"
            value={statistics.losingPlayerRanking.toString()}
          />
          <StatRow
            label="Winning Player Score"
            value={statistics.winningPlayerScore.toString()}
          />
          <StatRow
            label="Losing Player Score"
            value={statistics.losingPlayerScore.toString()}
          />
          <StatRow
            label="Number of Moves"
            value={statistics.numberOfMoves.toString()}
          />
          <StatRow
            label="White Pieces Captured"
            value={statistics.whitePiecesCaptured.join(", ") || "None"}
          />
          <StatRow
            label="Black Pieces Captured"
            value={statistics.blackPiecesCaptured.join(", ") || "None"}
          />
          <StatRow
            label="No. of White Pieces Captured"
            value={statistics.numWhitePiecesCaptured.toString()}
          />
          <StatRow
            label="No. of Black Pieces Captured"
            value={statistics.numBlackPiecesCaptured.toString()}
          />
          <StatRow
            label="Date/Time of Game End"
            value={new Date(statistics.gameEndDateTime).toLocaleString()}
          />
          <StatRow
            label="Time Limit of Game"
            value={`${statistics.timeLimit} minutes`}
          />
          <StatRow label="Room Number" value={statistics.roomNumber} />
        </div>

        {/* Close Button */}
        <div
          style={{
            marginTop: "12px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <button
            onClick={handleClose}
            style={{
              padding: "8px 30px",
              backgroundColor: "#7b1fa2",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Helper component for stat rows
const StatRow: React.FC<{
  label: string;
  value: string;
  highlight?: boolean;
}> = ({ label, value, highlight }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      padding: "5px 8px",
      backgroundColor: highlight ? "#e1bee7" : "white",
      borderRadius: "4px",
      border: highlight ? "2px solid #7b1fa2" : "1px solid #ddd",
    }}
  >
    <span style={{ fontWeight: "600", color: "#555", fontSize: "12px" }}>
      {label}:
    </span>
    <span
      style={{
        color: highlight ? "#6a1b9a" : "#333",
        fontSize: "12px",
        fontWeight: highlight ? "bold" : "normal",
      }}
    >
      {value}
    </span>
  </div>
);

// Utility function to get piece value from pieceValues.ts based on piece type and board size
const getPieceValue = (pieceType: string, boardSize: number): number => {
  const type = pieceType.toUpperCase();
  const pieceValues = PIECE_VALUES[type];

  if (!pieceValues) {
    return 1; // Default value for unknown pieces
  }

  // Find the value for the specific board size
  const boardSizeStr = `${boardSize}×${boardSize}`;
  const valueEntry = pieceValues.find(v => v.boardSize === boardSizeStr);

  if (valueEntry) {
    return valueEntry.value;
  }

  // Fallback to 8x8 value if board size not found
  const defaultEntry = pieceValues.find(v => v.boardSize === '8×8');
  return defaultEntry?.value || 1;
};

// Utility function to generate game statistics from game state
export const generateGameStatistics = (params: {
  gameName: string;
  boardSize: number;
  gameResult: string;
  winningPlayerId: string;
  losingPlayerId: string;
  winningPlayerColor: "white" | "black";
  losingPlayerColor: "white" | "black";
  winningPlayerRanking?: number;
  losingPlayerRanking?: number;
  numberOfMoves: number;
  capturedPieces: {
    white: Array<{ type: string; abbreviation?: string }>;
    black: Array<{ type: string; abbreviation?: string }>;
  };
  timeLimit: number;
  roomNumber: string;
}): GameStatisticsData => {
  return {
    gameName: params.gameName,
    boardSize: params.boardSize,
    gameResult: params.gameResult,
    winningPlayerId: params.winningPlayerId,
    losingPlayerId: params.losingPlayerId,
    winningPlayerColor: params.winningPlayerColor,
    losingPlayerColor: params.losingPlayerColor,
    winningPlayerRanking: params.winningPlayerRanking || 1000,
    losingPlayerRanking: params.losingPlayerRanking || 1000,
    // Calculate scores based on captured pieces value from pieceValues.ts
    // Use abbreviation (e.g., "QUN") for lookup, not full type name (e.g., "Queen")
    // Winner's score = value of pieces they captured (opponent's pieces)
    // If winner is white, they captured black pieces
    winningPlayerScore: params.winningPlayerColor === 'white'
      ? params.capturedPieces.black.reduce((sum, p) => sum + getPieceValue(p.abbreviation || p.type, params.boardSize), 0)
      : params.capturedPieces.white.reduce((sum, p) => sum + getPieceValue(p.abbreviation || p.type, params.boardSize), 0),
    losingPlayerScore: params.losingPlayerColor === 'white'
      ? params.capturedPieces.black.reduce((sum, p) => sum + getPieceValue(p.abbreviation || p.type, params.boardSize), 0)
      : params.capturedPieces.white.reduce((sum, p) => sum + getPieceValue(p.abbreviation || p.type, params.boardSize), 0),
    numberOfMoves: params.numberOfMoves,
    whitePiecesCaptured: params.capturedPieces.white.map((p) => p.type),
    blackPiecesCaptured: params.capturedPieces.black.map((p) => p.type),
    numWhitePiecesCaptured: params.capturedPieces.white.length,
    numBlackPiecesCaptured: params.capturedPieces.black.length,
    gameEndDateTime: new Date().toISOString(),
    timeLimit: params.timeLimit,
    roomNumber: params.roomNumber,
  };
};

export default GameStatistics;
